import {
    ACTIVITIES_FIELDS,
    COMPANIES_FIELDS,
    OPPORTUNITIES_FIELDS,
    CONTACTS_FIELDS,
    AGENDA_FIELDS,
    PRODUCTS_FIELDS,
    SALESORDERS_FIELDS,
} from 'constants/Fields';
import {
    ACTIVITIES,
    COMPANIES,
    OPPORTUNITIES,
    CONTACTS,
    AGENDA,
    PRODUCTS,
    SALESORDERS,
} from 'constants/Entities';
import Context from 'managers/Context';

const fieldsByEntity = {
    [ACTIVITIES.entity]: ACTIVITIES_FIELDS,
    [COMPANIES.entity]: COMPANIES_FIELDS,
    [OPPORTUNITIES.entity]: OPPORTUNITIES_FIELDS,
    [CONTACTS.entity]: CONTACTS_FIELDS,
    [AGENDA.entity]: AGENDA_FIELDS,
    [PRODUCTS.entity]: PRODUCTS_FIELDS,
    [SALESORDERS.entity]: SALESORDERS_FIELDS,
};

export const getCrudFieldConfig = (entity, fieldId) => {
    if (!entity || !fieldsByEntity[entity.entity]) return {};
    const fields = fieldsByEntity[entity.entity];
    if (!fields[fieldId]) return {};
    const field = fields[fieldId];

    return {
        id: field.id,
        serverId: field.serverId,
        fieldConfiguration: field.b2Name,
        fieldValidation: field.b4Name,
    };
};

export const getCrudFieldId = (entity, fieldId) => {
    if (!entity || !fieldsByEntity[entity.entity]) return '';
    const fields = fieldsByEntity[entity.entity];
    if (!fields[fieldId]) return '';
    return fields[fieldId].id;
};

const SF_CRUD_TYPE_GENERAL = '0';
const SF_CRUD_TYPE_INSERT = '1';
const SF_CRUD_TYPE_UPDATE = '2';

/**
 * Retrieves smart field properties based on parent field values and entity state.
 * Dynamically adjusts field properties depending on whether the entity is being created or updated.
 *
 * @param {Object} params - The function parameters
 * @param {Object} params.field - The field object containing smart field configuration
 * @param {Object} params.field.smartField - Smart field configuration with properties and parent field reference
 * @param {Object} params.configMap - Mapping between field IDs and their actual property names
 * @param {Object} params.data - The current entity data object
 * @param {boolean} params.isNewEntity - Whether this is a new entity being created (true) or an existing one being updated (false)
 * @returns {Object} - A properties object to apply to the field (may include hidden, readOnly, mandatory, etc.)
 */
export const getSmartFieldProps = ({ field, configMap, data, isNewEntity = false }) => {
    // Validate inputs
    if (!field || !field.smartField || !configMap || !data) {
        return {};
    }

    try {
        const { smartField } = field;
        const currentCrudType = isNewEntity ? SF_CRUD_TYPE_INSERT : SF_CRUD_TYPE_UPDATE;

        // Ensure parent field ID exists in config map
        if (!smartField.parentFieldId || !configMap[smartField.parentFieldId]) {
            return {};
        }

        const parentValue = data[configMap[smartField.parentFieldId]];
        // If parent value is undefined or null, return empty object
        if (parentValue === undefined || parentValue === null) {
            return {};
        }

        let finalValue = parentValue;
        // Extract actual value from array or object if needed
        if (Array.isArray(parentValue) && parentValue.length > 0) {
            finalValue = parentValue[0];
        } else if (typeof parentValue === 'object' && parentValue !== null) {
            finalValue = parentValue.value !== undefined ? parentValue.value : parentValue;
        }

        // If finalValue is undefined, null, or not a valid property key, return empty object
        if (
            finalValue === undefined ||
            finalValue === null ||
            (typeof finalValue !== 'string' && typeof finalValue !== 'number')
        ) {
            return {};
        }

        // Get properties from the smart field based on the final value
        const fieldProperties = smartField.properties?.[finalValue];

        if (fieldProperties) {
            const { crudType, visible, ...props } = fieldProperties;
            // Return properties based on CRUD type match
            return crudType === SF_CRUD_TYPE_GENERAL || crudType === currentCrudType
                ? { hidden: !visible, ...props }
                : {};
        }
    } catch (error) {
        console.error('Error in getSmartFieldProps:', error);
    }

    return {};
};

/**
 * Converts a semicolon-separated string of values into an array of objects with value and label properties.
 * Uses the server list data to find matching items and extract their descriptions.
 *
 * @param {Object} params - The function parameters
 * @param {string} params.list - The name of the server list to search in
 * @param {string} params.values - Semicolon-separated string of values to match
 * @returns {Array<Object>} - Array of objects with value and label properties, or empty array if no matches found
 */
export const getValuesFromList = ({ list, values }) => {
    if (!list || !values) return [];

    try {
        const state = Context.store.getState();
        const data = state?.serverList?.[list]?.data || [];

        return (typeof values === 'string' ? values : String(values))
            .split(';')
            .filter(Boolean)
            .map((item) => {
                const listItem = data.find((li) => li && li.value === item);
                return listItem
                    ? {
                          value: listItem.value,
                          label: listItem.descripcion || listItem.label || listItem.value,
                      }
                    : null;
            })
            .filter(Boolean);
    } catch (error) {
        console.error('Error in getValuesFromList:', error);
        return [];
    }
};
